import api from './api';
import { API_ENDPOINTS } from '../constants';

export interface Leave {
  id: string;
  user_id: string;
  store_id: string;
  cluster_id: string;
  leave_type: 'sick_leave' | 'casual_leave' | 'earned_leave' | 'maternity_leave' | 'paternity_leave' | 'emergency_leave';
  start_date: string;
  end_date: string;
  total_days: number;
  reason: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  applied_date: string;
  approved_by?: string;
  approved_date?: string;
  rejection_reason?: string;
  is_emergency: boolean;
  attachment_url?: string;
  comments?: string;
  user?: {
    id: string;
    first_name: string;
    last_name: string;
    employee_id: string;
    profile_image_url?: string | null;
    store?: {
      id: string;
      name: string;
    };
    cluster?: {
      id: string;
      name: string;
    };
  };
  store?: {
    id: string;
    name: string;
    code: string;
  };
  cluster?: {
    id: string;
    name: string;
    code: string;
  };
  approver?: {
    id: string;
    first_name: string;
    last_name: string;
    employee_id: string;
  };
}

export interface CreateLeaveRequest {
  leave_type: string;
  start_date: string;
  end_date: string;
  reason: string;
  is_emergency?: boolean;
  attachment_url?: string;
}

export interface ApproveLeaveRequest {
  status: 'approved' | 'rejected';
  comments?: string;
  rejection_reason?: string;
}

export interface LeaveFilters {
  status?: string;
  leave_type?: string;
  store_id?: string;
  cluster_id?: string;
  start_date?: string;
  end_date?: string;
  is_emergency?: boolean;
  page?: number;
  limit?: number;
}

export interface LeaveBalance {
  sick_leave: number;
  casual_leave: number;
  earned_leave: number;
  maternity_leave: number;
  paternity_leave: number;
  emergency_leave: number;
  total_available: number;
  total_used: number;
}

export interface LeaveStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  cancelled: number;
  by_type: Record<string, number>;
  by_month: Array<{
    month: string;
    count: number;
  }>;
}

const leaveService = {
  /**
   * Get leaves with filters
   */
  getLeaves: async (filters: LeaveFilters = {}): Promise<{
    leaves: Leave[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      data: {
        leaves: Leave[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
        };
      };
    }>(API_ENDPOINTS.LEAVES.LIST, {
      params: filters,
    });
    return response.data.data;
  },

  /**
   * Get leave by ID
   */
  getLeaveById: async (id: string): Promise<Leave> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      data: Leave;
    }>(API_ENDPOINTS.LEAVES.GET_BY_ID(id));
    return response.data.data;
  },

  /**
   * Create leave request
   */
  createLeave: async (leaveData: CreateLeaveRequest): Promise<Leave> => {
    const response = await api.post<{
      success: boolean;
      message: string;
      data: Leave;
    }>(API_ENDPOINTS.LEAVES.CREATE, leaveData);
    return response.data.data;
  },

  /**
   * Update leave request
   */
  updateLeave: async (id: string, leaveData: Partial<CreateLeaveRequest>): Promise<Leave> => {
    const response = await api.put<{
      success: boolean;
      message: string;
      data: Leave;
    }>(API_ENDPOINTS.LEAVES.UPDATE(id), leaveData);
    return response.data.data;
  },

  /**
   * Cancel leave request
   */
  cancelLeave: async (id: string): Promise<Leave> => {
    const response = await api.patch<{
      success: boolean;
      message: string;
      data: Leave;
    }>(API_ENDPOINTS.LEAVES.CANCEL(id));
    return response.data.data;
  },

  /**
   * Approve or reject leave request
   */
  approveLeave: async (id: string, approvalData: ApproveLeaveRequest): Promise<Leave> => {
    const response = await api.patch<{
      success: boolean;
      message: string;
      data: Leave;
    }>(API_ENDPOINTS.LEAVES.APPROVE(id), approvalData);
    return response.data.data;
  },

  /**
   * Get leave balance for current user
   */
  getLeaveBalance: async (): Promise<LeaveBalance> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      data: LeaveBalance;
    }>(API_ENDPOINTS.LEAVES.BALANCE);
    return response.data.data;
  },

  /**
   * Get leave calendar for current user
   */
  getLeaveCalendar: async (filters: LeaveFilters = {}): Promise<{
    calendar: Array<{
      date: string;
      leaves: Leave[];
    }>;
  }> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      data: {
        calendar: Array<{
          date: string;
          leaves: Leave[];
        }>;
      };
    }>(API_ENDPOINTS.LEAVES.CALENDAR, {
      params: filters,
    });
    return response.data.data;
  },

  /**
   * Get pending leaves (admin only)
   */
  getPendingLeaves: async (filters: LeaveFilters = {}): Promise<{
    leaves: Leave[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      data: {
        leaves: Leave[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
        };
      };
    }>(API_ENDPOINTS.LEAVES.PENDING, {
      params: filters,
    });
    return response.data.data;
  },

  /**
   * Get leave reports (admin only)
   */
  getLeaveReports: async (filters: LeaveFilters = {}): Promise<{
    stats: LeaveStats;
    leaves: Leave[];
  }> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      data: {
        stats: LeaveStats;
        leaves: Leave[];
      };
    }>(API_ENDPOINTS.LEAVES.REPORTS, {
      params: filters,
    });
    return response.data.data;
  },

  /**
   * Export leave data
   */
  exportLeaveData: async (filters: LeaveFilters = {}): Promise<Blob> => {
    const response = await api.get(API_ENDPOINTS.LEAVES.EXPORT, {
      params: filters,
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Get leave statistics
   */
  getLeaveStats: async (filters: LeaveFilters = {}): Promise<LeaveStats> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      data: LeaveStats;
    }>(API_ENDPOINTS.LEAVES.STATS, {
      params: filters,
    });
    return response.data.data;
  },

  /**
   * Get user leave history
   */
  getUserLeaveHistory: async (userId: string, filters: LeaveFilters = {}): Promise<{
    leaves: Leave[];
    stats: LeaveStats;
  }> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      data: {
        leaves: Leave[];
        stats: LeaveStats;
      };
    }>(API_ENDPOINTS.LEAVES.USER_HISTORY(userId), {
      params: filters,
    });
    return response.data.data;
  },
};

export default leaveService;
